import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

/**
 * Poner una contraseña nueva.
 *
 * Esta pantalla faltaba, y sin ella "¿olvidaste tu contraseña?" no
 * servía para nada: el correo llegaba, la persona tocaba el enlace, y
 * caía en una pantalla que no le pedía la contraseña nueva. Se quedaba
 * afuera igual.
 *
 * Cómo funciona: el enlace del correo abre la app con una sesión
 * temporal de recuperación. Con esa sesión —y solo con ella— se puede
 * cambiar la contraseña.
 */
export default function RecuperarPage() {
  const navigate = useNavigate();
  const [listo, setListo] = useState(false);
  const [sinSesion, setSinSesion] = useState(false);
  const [clave, setClave] = useState('');
  const [repetida, setRepetida] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [hecho, setHecho] = useState(false);

  useEffect(() => {
    // El enlace del correo trae el permiso en la dirección. La librería
    // lo procesa sola, pero tarda un instante: por eso se escucha el
    // aviso además de preguntar una vez.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((evento, sesion) => {
      if (evento === 'PASSWORD_RECOVERY' || sesion) {
        setListo(true);
        setSinSesion(false);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setListo(true);
      } else {
        // Se espera un poco antes de rendirse: si el enlace es válido,
        // la sesión aparece en menos de un segundo.
        setTimeout(() => {
          supabase.auth.getSession().then(({ data: { session: otra } }) => {
            if (otra) setListo(true);
            else setSinSesion(true);
          });
        }, 1500);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (clave.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (clave !== repetida) {
      setError('Las dos contraseñas no coinciden.');
      return;
    }

    setGuardando(true);
    const { error: fallo } = await supabase.auth.updateUser({ password: clave });
    setGuardando(false);

    if (fallo) {
      setError(
        fallo.message.includes('expired') || fallo.message.includes('invalid')
          ? 'El enlace venció. Pide uno nuevo desde "¿Olvidaste tu contraseña?".'
          : 'No se pudo guardar. Intenta de nuevo.',
      );
      return;
    }

    setHecho(true);
    setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md">
        <h1
          className="text-2xl font-bold text-gray-800 mb-1 text-center"
          style={{ fontFamily: '"Pacifico", serif' }}
        >
          Agenda Padres Bebés
        </h1>
        <p className="text-sm text-gray-500 mb-6 text-center">de SOS Padres</p>

        {hecho ? (
          <div className="text-center space-y-3">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
              <i className="ri-check-line text-3xl text-green-600"></i>
            </div>
            <h2 className="text-xl font-semibold text-green-700">Contraseña cambiada</h2>
            <p className="text-gray-600">Entrando a tu cuenta...</p>
          </div>
        ) : sinSesion && !listo ? (
          <div className="text-center space-y-3">
            <div className="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center">
              <i className="ri-time-line text-3xl text-amber-600"></i>
            </div>
            <h2 className="text-xl font-semibold text-gray-800">Este enlace ya no sirve</h2>
            <p className="text-gray-600 text-sm">
              Los enlaces para cambiar la contraseña duran poco, por seguridad. Pide uno
              nuevo y úsalo apenas te llegue.
            </p>
            <button
              onClick={() => navigate('/auth')}
              className="w-full bg-blue-600 text-white py-3 rounded-xl font-medium"
            >
              Volver a entrar
            </button>
          </div>
        ) : !listo ? (
          <div className="text-center py-6">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Comprobando el enlace...</p>
          </div>
        ) : (
          <form onSubmit={guardar} className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-800 text-center">
              Pon tu contraseña nueva
            </h2>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Contraseña nueva</label>
              <input
                type="password"
                value={clave}
                onChange={(e) => setClave(e.target.value)}
                placeholder="Al menos 6 caracteres"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-400"
                autoComplete="new-password"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Repítela</label>
              <input
                type="password"
                value={repetida}
                onChange={(e) => setRepetida(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-400"
                autoComplete="new-password"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={guardando}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 rounded-xl font-medium disabled:opacity-50"
            >
              {guardando ? 'Guardando...' : 'Guardar contraseña'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
